import { useState, useEffect, useRef } from 'react';
import { AxiosRequestConfig } from 'axios';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
// Same signature, answered by the new backend. See legacy-deal-api.ts.
import {
  postDealCalc,
  PostDealCalcRequestType,
} from 'service/moneeflo/legacy-deal-api';
import { toast } from 'sonner';
import {
  setTcs,
  setServicePartnerId,
  setForeignBankRates,
  setTTRates,
  setServicePartnerMode,
  setSettlementRates,
  setcal_total_charges,
  setcal_profit,
  setcal_amount_inr,
  setcal_gst,
  setcal_tt_charges,
  setcal_total_payable,
  setcal_foreign_bank_charges,
  setcal_your_rate,
  setCustomer_rate,
  setAtALoss,
  setcal_customer_rate,
} from '../../../redux/features/CreateDeal';

export const useDebounce = (
  callback: () => void,
  delay: number,
  dependencies: any[]
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, dependencies);
};

export const useDealCalculation = () => {
  const [isCalcLoading, setIsCalcLoading] = useState(false);
  const [rate, setRate] = useState<number>(0);
  const [limitCustomerRate, setLimitCustomerRate] = useState(0);
  const [limitCustomerRate2, setLimitCustomerRate2] = useState(0);
  
  const dispatch = useDispatch();
  const level = localStorage.getItem("level");
  
  const {
    fx_charges,
    amount,
    currency,
    purpose_id,
    purpose_id_copy,
    type,
    created_by,
    add_on_rates,
    country,
    partner_status,
    partner_margin,
    partner_walkin_margin,
    customer_rate,
    service_partner_id,
    custom_settlement_rate
  } = useAppSelector((state) => state.CreateDeal);

  const fetchCalc = async (servicePartner?: string | number) => {
    if (!fx_charges || !amount || !currency || !purpose_id) {
      return;
    }

    setIsCalcLoading(true);

    try {
      const auth = localStorage.getItem("authToken");
      if (!auth) return;
      
      const authToken = JSON.parse(auth);
      const payload: AxiosRequestConfig<PostDealCalcRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: {
          purpose_id: purpose_id_copy === "" ? 1 : parseInt(purpose_id_copy),
          currency: currency === "" ? "USD" : currency,
          amount: parseInt(amount) || 0,
          type,
          created_by,
          tcs_type: 1,
          discount: 0,
          addon_rates: add_on_rates,
          country,
          service_partner_id: service_partner_id,
          settlement_rates: custom_settlement_rate || undefined,
          partner_margin: partner_status === "pending" ? (partner_margin || "0") as string : "",
          partner_walkin_margin: partner_status === "pending" ? (partner_walkin_margin || "0") as string : "",
          fx_charges,
        },
      };

      if (
        currency &&
        purpose_id &&
        created_by !== "-1" &&
        (partner_status !== "pending" ||
          (partner_status === "pending" && partner_margin && partner_walkin_margin))
      ) {
        const res = await postDealCalc(payload);
        
        // Update all calculation states
        dispatch(setTcs(res?.data.tcs));
        dispatch(setServicePartnerId(res.data.service_partner));
        dispatch(setForeignBankRates(res.data.foreign_bank_charges));
        dispatch(setTTRates(res.data.tt_charges));
        dispatch(setServicePartnerMode(res.data.service_partner_mode));
        
        if (res.data.service_partner_mode === "online") {
          dispatch(setSettlementRates(res.data.settlement_rates));
        } else {
          dispatch(setSettlementRates(""));
        }
        
        dispatch(
          setcal_total_charges(
            parseFloat(res.data.payable_amount) - parseFloat(res.data.base_amount)
          )
        );
        
        // Set profit based on level
        const profitKey = `l${level}` as keyof typeof res.data.profit;
        if (res.data.profit[profitKey]) {
          dispatch(setcal_profit(res.data.profit[profitKey]));
        }
        
        dispatch(setcal_amount_inr(res.data.base_amount));
        dispatch(setcal_gst(res.data.gst));
        dispatch(setcal_tt_charges(res.data.tt_charges_with_gst));
        dispatch(setcal_total_payable(res.data.payable_amount));
        dispatch(setcal_foreign_bank_charges(res.data.foreign_bank_charges_with_gst));
        
        // The standard rate for this corridor. The backend hands it over
        // because it is the one figure the screen needs that a partner is
        // allowed to see; adding up the settlement rate and the commissions
        // only works for someone who may see what the currency costs us.
        const baseRate = parseFloat(res?.data?.base_customer_rate);

        // What this organization buys at: the customer's rate less its own
        // cut. Same answer at every level — an L2 is left with the
        // settlement rate plus L1's commission — and it needs nothing the
        // caller is not allowed to see, which the old sum did.
        const amountSent = parseFloat(amount) || 0;
        const ownCommission = Number(res.data.profit[profitKey]) || 0;
        const ownRatePerUnit = amountSent > 0 ? ownCommission / amountSent : 0;
        const yourRate = (baseRate - ownRatePerUnit).toFixed(4);

        dispatch(setcal_your_rate(yourRate));

        dispatch(
          setAtALoss({
            atALoss: Boolean(res.data.at_a_loss),
            loss:
              res.data.margin === null || res.data.margin === undefined
                ? null
                : -Number(res.data.margin),
          }),
        );
        
        // The floor the screen holds a typed rate to. Commission is already
        // in the standard rate, so both limits are that rate; they stay two
        // fields because the screen compares against both.
        setLimitCustomerRate(baseRate);
        setLimitCustomerRate2(baseRate);

        setRate(baseRate);
        
        dispatch(setCustomer_rate(Number(res.data.customer_rate)?.toFixed(4)));
        dispatch(setcal_customer_rate(parseFloat(res.data.customer_rate)?.toFixed(4)?.toString()));
        
        localStorage.setItem("service_partner_id", JSON.stringify(res.data.service_partner));
      }
    } catch (err: any) {
      toast.warning(err?.response?.data?.message || 'Calculation failed');
    } finally {
      setIsCalcLoading(false);
    }
  };

  // Debounced calculation
  useDebounce(
    () => {
      if (!isCalcLoading) {
        fetchCalc();
      }
    },
    1000,
    [amount, created_by, fx_charges, customer_rate, currency, purpose_id, purpose_id_copy, partner_margin, partner_walkin_margin, add_on_rates, service_partner_id, custom_settlement_rate]
  );

  return {
    isCalcLoading,
    rate,
    limitCustomerRate,
    limitCustomerRate2,
    fetchCalc,
  };
};